import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Progress } from './progress';
import { BarChart3, FileText, RotateCcw, ChevronLeft } from 'lucide-react';
import api, { generateAIReport } from '../sever/Api';
import { loadStripe } from '@stripe/stripe-js';
// Using Stripe Checkout redirect for one-time report
import { useNavigate } from 'react-router-dom';
import ViaReport from './ViaReport';


const TestResultUI = ({
  testTitle,
  testDescription,
  results,
  onRetake,
  onBack,
  showBackButton = false,
  children,
  aiReportData
}) => {
  const navigate = useNavigate();
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReportError, setAiReportError] = useState(null);
  const [aiReportText, setAiReportText] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  // No Elements modal; using redirect-to-checkout for one-time report

  // For subscription plans we still use redirectToCheckout elsewhere
  const stripe = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

  // Reuse existing Stripe checkout flow
  const startCheckout = async (plan) => {
    try {
      const successUrl = `${window.location.origin}/personality-test?checkout=success&plan=${encodeURIComponent(plan)}`;
      const cancelUrl = `${window.location.origin}/personality-test?checkout=cancelled&plan=${encodeURIComponent(plan)}`;
      const response = await api.post('/api/billing/create-checkout-session', {
        plan,
        successUrl,
        cancelUrl,
      });
      const { id, url } = response.data || {};
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (id && publishableKey) {
        const stripe = await loadStripe(publishableKey);
        const { error } = await stripe.redirectToCheckout({ sessionId: id });
        if (error) {
          console.error('Stripe redirect error:', error);
          if (url) window.location.href = url;
        }
      } else if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Checkout error', error);
      const serverMessage = error?.response?.data?.message;
      const serverError = error?.response?.data?.error;
      alert(serverMessage || serverError || 'Unable to start checkout right now. Please try again later.');
    }
  };

  // One-time $8 report via Stripe Checkout redirect
  const startReportCheckout = async () => {
    try {
      // Return user to the SAME test page, ensure results are visible and trigger detailed view
      const success = new URL(window.location.href);
      success.searchParams.set('showResults', 'true');
      success.searchParams.set('reportCheckout', 'success');
      const successUrl = success.toString();

      const cancel = new URL(window.location.href);
      cancel.searchParams.set('showResults', 'true');
      cancel.searchParams.set('reportCheckout', 'cancelled');
      const cancelUrl = cancel.toString();
      const { data } = await api.post('/api/billing/create-report-checkout-session', { successUrl, cancelUrl });
      const { id, url } = data || {};
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (id && publishableKey) {
        const stripe = await loadStripe(publishableKey);
        const { error } = await stripe.redirectToCheckout({ sessionId: id });
        if (error) {
          console.error('Stripe redirect error:', error);
          if (url) window.location.href = url;
        }
      } else if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Report checkout error', error);
      const serverMessage = error?.response?.data?.message;
      const serverError = error?.response?.data?.error;
      alert(serverMessage || serverError || 'Unable to start checkout. Please try again.');
    }
  };

  useEffect(() => {
    const autoFetch = async () => {
      if (showDetailedReport && aiReportData && !aiReportText && !aiReportLoading) {
        try {
          setAiReportLoading(true);
          setAiReportError(null);
          const { report } = await generateAIReport(aiReportData);
          setAiReportText(report);
        } catch (err) {
          setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
        } finally {
          setAiReportLoading(false);
        }
      }
    };
    autoFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailedReport]);

  // Detect Stripe Checkout return for report and unlock report for this user
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get('reportPayment');
    const reportCheckout = params.get('reportCheckout');
    if (paid === 'success' || reportCheckout === 'success') {
      try {
        localStorage.setItem('reportPaid', 'true');
      } catch (_) { }
      setShowPaywall(false);
      setShowDetailedReport(true);
      // Clean URL
      const url = new URL(window.location.href);
      url.searchParams.delete('reportPayment');
      url.searchParams.delete('reportCheckout');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  if (showDetailedReport) {
    return (
      <div className="min-h-screen p-4">
        <div className="mx-auto space-y-6" style={{ width: '85%' }}>
          {/* VIA Structured Report (10 pages) */}
          {aiReportData?.testType === 'viacharacter' && (
            <div id="via-report-print">
              <Card className='bg-[#ffffff]'>
                <CardHeader>
                  <div className="flex items-center justify-between">


                  </div>
                </CardHeader>
                <CardContent>
                  <ViaReport
                    participantName={(function () {
                      try {
                        const userStr = localStorage.getItem('user');
                        const user = userStr ? JSON.parse(userStr) : null;
                        return user?.name || user?.email || 'Participant';
                      } catch (_) { return 'Participant'; }
                    })()}
                    scores={aiReportData?.scores || {}}
                  />

                </CardContent>
              </Card>
            </div>
          )}

          {/* AI Narrative (optional) */}
          {/* <Card className='bg-[#ffffff]'>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-semibold">We are collecting your results</CardTitle>
                  <CardDescription>Personalized narrative based on your results</CardDescription>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${aiReportLoading ? 'bg-blue-50 text-blue-700 border-blue-200' : aiReportText ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                  {aiReportLoading ? 'Generating…' : aiReportText ? 'Ready' : 'Not generated'}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-3 gap-3">
                <div className="text-xs text-gray-500">
                  {aiReportLoading ? 'Preparing your narrative' : aiReportText ? 'Report generated' : 'Click Generate to create your report'}
                </div>
                {!aiReportText && (
                  <Button
                    onClick={async () => {
                      try {
                        setAiReportLoading(true);
                        setAiReportError(null);
                        const { report } = await generateAIReport(aiReportData || {});
                        setAiReportText(report);
                      } catch (err) {
                        setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
                      } finally {
                        setAiReportLoading(false);
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={aiReportLoading}
                  >
                    Generate Report
                  </Button>
                )}
              </div>

              {aiReportError && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {aiReportError}
                </div>
              )}

              {aiReportLoading && (
                <div className="space-y-2 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                  <div className="h-4 bg-gray-200 rounded w-10/12"></div>
                  <div className="h-4 bg-gray-200 rounded w-9/12"></div>
                  <div className="h-4 bg-gray-200 rounded w-8/12"></div>
                </div>
              )}

              {!aiReportLoading && aiReportText && (
                <div className="rounded-lg border border-gray-200 bg-white p-5">
                  <div className="max-w-none whitespace-pre-wrap text-gray-900 text-[15px] md:text-base leading-7 tracking-normal">
                    {aiReportText}
                  </div>
                </div>
              )}

              {!aiReportLoading && !aiReportText && !aiReportError && (
                <div className="text-sm text-gray-600">
                  Click Generate to create your personalized narrative. You can copy or download it once ready.
                </div>
              )}
            </CardContent>
          </Card> */}
          <div className="flex items-center gap-2">
          <Button  onClick={() => setShowDetailedReport(false)} variant="outline" className="ml-auto bg-[#ffffff]">
              <ChevronLeft className="mr-2 w-4 h-4" />
              Back to Results
            </Button>
            <Button style={{width: '90%'}}
              onClick={() => window.print()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Download PDF
            </Button>
            
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="mx-auto" style={{ width: '85%' }}>
        <Card className='bg-[#ffffff]'>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <BarChart3 className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-3xl font-bold">Your {testTitle} Results</CardTitle>
            <CardDescription>{testDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 ">
            {results}
            <div className="flex gap-4 pt-6">
              <Button
                onClick={() => {
                  try {
                    const userStr = localStorage.getItem('user');
                    const user = userStr ? JSON.parse(userStr) : null;
                    const plan = user?.plan || 'FREE';
                    const role = user?.role || '';
                    const isUnlimited = plan === 'PREMIUM' || role === 'admin';
                    const reportPaid = localStorage.getItem('reportPaid') === 'true';
                    if ((plan === 'FREE' || !user) && !reportPaid) {
                      setShowPaywall(true);
                      return;
                    }
                    // Allow STANDARD/PROFESSIONAL/PREMIUM to view
                    setShowDetailedReport(true);
                  } catch (_) {
                    const reportPaid = localStorage.getItem('reportPaid') === 'true';
                    if (!reportPaid) {
                      setShowPaywall(true);
                    } else {
                      setShowDetailedReport(true);
                    }
                  }
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <FileText className="mr-2 w-4 h-4" />
                View Detailed Report
              </Button>
              {showBackButton && (
                <Button
                  onClick={onBack}
                  variant="outline"
                  className="bg-[#ffffff]"
                >
                  <ChevronLeft className="mr-2 w-4 h-4" />
                  Back
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onRetake}
                className="bg-[#ffffff]"
              >
                <RotateCcw className="mr-2 w-4 h-4" />
                Retake Test
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      {showPaywall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ height: '100vh', overflow: 'auto' }}>
          {/* overlay */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowPaywall(false)}
          />

          {/* modal */}
          <div className="relative z-10 w-[92%] max-w-md bg-white rounded-xl shadow-xl overflow-hidden">
            {/* close button */}
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              onClick={() => setShowPaywall(false)}
            >
              ✕
            </button>

            {/* header */}
            <div className="text-center p-6">
              <h2 className="text-xl font-bold">Unlock Detailed Report</h2>
              <p className="text-sm text-gray-600">
                Choose a plan that suits you best
              </p>
            </div>

            {/* plans */}
            <div className="space-y-4 px-6 pb-6">
              <div className="rounded-lg bg-gradient-to-r from-orange-400 to-yellow-400 text-white flex justify-between items-center px-4 py-3 shadow cursor-pointer" onClick={startReportCheckout}>
                <span className="font-semibold">Get Your Complete Report</span>
                <span className="text-lg font-bold">$8</span>
              </div>

              <div className="rounded-lg bg-gradient-to-r from-blue-400 to-blue-600 text-white flex justify-between items-center px-4 py-3 shadow cursor-pointer" onClick={() => startCheckout('STANDARD')}>
                <span className="font-semibold">Standard</span>
                <span className="text-lg font-bold">$15</span>
              </div>

              <div className="rounded-lg bg-gradient-to-r from-purple-400 to-pink-500 text-white flex justify-between items-center px-4 py-3 shadow cursor-pointer" onClick={() => startCheckout('PROFESSIONAL')}>
                <span className="font-semibold">All-Access Professional</span>
                <span className="text-lg font-bold">$144.99</span>
              </div>

              <div className="rounded-lg bg-gradient-to-r from-pink-400 to-red-500 text-white flex justify-between items-center px-4 py-3 shadow cursor-pointer" onClick={() => startCheckout('PREMIUM')}>
                <span className="font-semibold">Premium Plus</span>
                <span className="text-lg font-bold">$199.99</span>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default TestResultUI;
